import { useInfiniteQuery, useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { buildFixedAssetsPayload } from '~/api/bookkeeping/fixed-asset-payload';
import { fetchFixedAssetsSpecies } from '~/api/bookkeeping/fixed-assets-species';
import {
  bulkBookkeepingTransactionsRegistration,
  createVATRegistration,
  deleteVATRegistrationById,
  deleteVATRegistrationsByDocumentId,
  fetchBillsToRegister,
  fetchVATRegistration,
  fetchVATRegistrationNumber,
  fetchVATRegistrations,
  previewVATRegistrationNumber,
  registrationsPageSize,
  updateVATRegistration,
} from '~/api/bookkeeping/vat-registrations';
import { queryClient } from '~/client/queryClient';
import { useActiveCompanyId } from '~/hooks/useActiveCompany';
import { captureException } from '~/hooks/useExceptionLogger';
import {
  decorateDocumentsWithAIVATRegistrationProposal,
  decorateDocumentsWithEInvoices,
  decorateDocumentsWithVATRegistrationProposal,
} from '~/services/bookkeeping/bookkeeping-invoices';
import { useVATBooks } from '~/services/bookkeeping/vat-books';
import { useVATCodesById } from '~/services/bookkeeping/vat-codes';
import { Document, DocumentDirection, DocumentQueryKeys, DocumentType, EInvoiceType } from '~/types/document';
import { CompanyFixedAssetSpecies, FixedAssetsSpeciesQueryKeys } from '~/types/fixed-asset';
import {
  BookkeepingAdjustmentSourceType,
  BookkeepingTransactionQueryKeys,
  CreateBookkeepingTransaction,
} from '~/types/general-bookkeeping';
import { ItalianEInvoice } from '~/types/invoicing';
import { MetaDataCursorPage } from '~/types/jsonapi';
import { assertIsDefined, isDefined } from '~/types/typeGuards';
import {
  BookkeepingInvoicesQueryKeys,
  DocumentWithVATRegistrations,
  FetchBillsToRegisterFilters,
  RegistrationPurchaseType,
  UpdateVATEntry,
  UpdateVATRegistration,
  VATBook,
  VATBookType,
  VATCode,
  VATEntriesQueryKeys,
  VATProposalItemType,
  VATRegistration,
  VATRegistrationEntry,
  VATRegistrationsFilters,
  VATRegistrationsQueryKeys,
} from '~/types/vat-bookkeeping';
import { type AdjustmentDescriptionFn, buildAdjustmentPayload } from '~/utils/bookkeeping/adjustments';
import { isReverseChargeVatCode } from '~/utils/bookkeeping/registrations';
import {
  negateEntries,
  useBookkeepingEntriesForVATRegistration,
} from '~/utils/bookkeeping/vat-registration-bookkeeping';
import { getEInvoiceBody } from '~/utils/invoicing';
import { absMoney, multiplyMoney, sumMoney } from '~/utils/money';
import { VATEntryForm, VATRegistrationFormOutput } from '~/views/bookkeeping/VAT-registration/VATRegistrationSchema';
import { buildDraft } from '~/views/bookkeeping/VAT-registration/fixed-asset/fixedAssetFormSchema';
import { fetchDocument } from '../document';

const ensureCompanySpecies = (companyId: string): Promise<CompanyFixedAssetSpecies[]> =>
  queryClient.ensureQueryData({
    queryKey: FixedAssetsSpeciesQueryKeys.byCompany(companyId),
    queryFn: () => fetchFixedAssetsSpecies(companyId),
  });

const mapEntriesToPayload = (
  entries: VATEntryForm[],
  vatCodeById: Map<string, VATCode>,
  companySpecies: CompanyFixedAssetSpecies[],
  registrationDate: Date,
  isExpense: boolean,
  includeAdjustments = true,
  isCreditNote = false,
  getDescription: AdjustmentDescriptionFn,
) => {
  return entries.map(({ items, fixedAsset, adjustmentAccountId, ...entry }) => {
    const adjustment = includeAdjustments
      ? buildAdjustmentPayload(
          adjustmentAccountId ?? null,
          entry.bookkeepingAccountId,
          registrationDate,
          entry.purchasePeriodStart ?? null,
          entry.purchasePeriodEnd ?? null,
          isExpense,
          sumMoney(
            absMoney(entry.taxableAmount),
            multiplyMoney(
              absMoney(entry.taxAmount),
              (100 - (vatCodeById.get(entry.vatCodeId)?.vatDeductibility ?? 100)) / 100,
            ),
          ),
          BookkeepingAdjustmentSourceType.VAT_ENTRY,
          getDescription,
        )
      : null;
    const bookkeepingAdjustment =
      isCreditNote && adjustment
        ? {
            ...adjustment,
            bookkeepingTransactions: adjustment.bookkeepingTransactions.map((tx) => ({
              ...tx,
              bookkeepingEntries: negateEntries(tx.bookkeepingEntries),
            })),
          }
        : adjustment;
    return {
      ...entry,
      documentLineItems: items.map(({ lineNumber, itemProposalId, itemType }) => ({
        lineNumber: Number(lineNumber),
        itemProposalId: itemProposalId ?? null,
        itemType: itemType ?? VATProposalItemType.Product,
      })),
      fixedAssets:
        entry.purchaseType === RegistrationPurchaseType.DepreciableAssets ||
        entry.purchaseType === RegistrationPurchaseType.NonDepreciableCapitalAssets
          ? buildFixedAssetsPayload(
              (fixedAsset ?? []).map((values) => buildDraft(values, companySpecies)),
              {
                taxableAmount: entry.taxableAmount,
                taxAmount: entry.taxAmount,
                vatDeductibility: vatCodeById.get(entry.vatCodeId)?.vatDeductibility,
              },
            )
          : [],
      bookkeepingAdjustment,
    };
  });
};

const entriesForRegistration = (
  entries: VATEntryForm[],
  vatBookId: string | null | undefined,
  reverseCharge: boolean,
  document: Document | null,
  vatBooksById: Map<string, VATBook>,
  vatCodesById: Map<string, VATCode>,
): VATEntryForm[] => {
  // There are cases where a received invoice (not a self-invoice)
  // includes items with reverse charge VAT nature codes.
  // In those cases we need to create a Sales book entry *only*
  // for these items, and not for the whole document.
  const areReverseChargeSalesEntries =
    reverseCharge &&
    document?.direction === DocumentDirection.received &&
    document.documentType !== DocumentType.SelfInvoice &&
    vatBooksById.get(vatBookId ?? '')?.bookType === VATBookType.Sales;

  if (!areReverseChargeSalesEntries) {
    return entries;
  }

  return entries.filter((entry) => isReverseChargeVatCode(vatCodesById.get(entry.vatCodeId)));
};

export const useDocumentVATRegistrations = (documentId: Document['id']) => {
  const companyId = useActiveCompanyId();
  return useSuspenseQuery({
    queryKey: VATRegistrationsQueryKeys.documentRegistrations(documentId),
    queryFn: async () => {
      const { registrations } = await fetchVATRegistrations(companyId, { documentId });
      return registrations;
    },
  });
};

export const useVATRegistrationById = (registrationId: VATRegistration['id']) => {
  return useSuspenseQuery({
    queryKey: VATRegistrationsQueryKeys.item(registrationId),
    queryFn: () => fetchVATRegistration(registrationId),
  });
};

export const useVATRegistrations = (filters: VATRegistrationsFilters, disabled = false) => {
  const companyId = useActiveCompanyId();
  return useInfiniteQuery({
    queryKey: VATRegistrationsQueryKeys.list(companyId, filters),
    queryFn: ({ pageParam }) => fetchVATRegistrations(companyId, filters, { cursor: pageParam }),
    getNextPageParam: ({
      page: { cursor },
      registrations,
    }: {
      page: MetaDataCursorPage;
      registrations: VATRegistration[];
    }) => {
      if (registrations.length >= registrationsPageSize) {
        return cursor;
      }
      return undefined;
    },
    enabled: !disabled,
    initialPageParam: '',
  });
};

interface CreateVATRegistrationPayload {
  document: Document | null;
  direction: DocumentDirection;
  eInvoice?: ItalianEInvoice | null;
  number: number;
  secondaryNumber: number | null;
  values: VATRegistrationFormOutput;
}

const hasSelfInvoiceBookepingRegistrationException = (type?: EInvoiceType | null): boolean => {
  return (
    type === EInvoiceType.AutofatturaServiziEsteri ||
    type === EInvoiceType.AutofatturaBeniEU ||
    type === EInvoiceType.AutofatturaBeniEsteri
  );
};

const getCounterpartId = (document: Document | null, values: CreateVATRegistrationPayload['values']) => {
  if (document) {
    return document.counterpart?.id ?? null;
  }
  return values.registration.counterpartId ?? null;
};

export const useCreateVATRegistration = () => {
  const companyId = useActiveCompanyId();
  const generateBookkeepingEntries = useBookkeepingEntriesForVATRegistration();
  const { data: vatBooks } = useVATBooks();
  const { data: vatCodesById } = useVATCodesById();
  const { t } = useTranslation('bookkeeping');

  return useMutation({
    mutationFn: async ({
      document,
      direction,
      eInvoice,
      number,
      secondaryNumber,
      values,
    }: CreateVATRegistrationPayload) => {
      const eInvoiceType =
        eInvoice &&
        document &&
        getEInvoiceBody(eInvoice, document.number!).datiGenerali.datiGeneraliDocumento.tipoDocumento;

      const bookkeepingEntries = generateBookkeepingEntries({
        document,
        direction,
        hasVatRegistrationException: hasSelfInvoiceBookepingRegistrationException(eInvoiceType) || values.reverseCharge,
        vatEntries: values.entries,
        bookkeepingAccountId: values.registration.bookkeepingAccountId,
        counterpartId: values.registration.counterpartId,
      });

      const bookkeepingTransaction: Omit<CreateBookkeepingTransaction, 'companyId'> = {
        date: values.registration.date,
        description: values.description,
        bookkeepingEntries,
        transactionIds: [],
      };

      const counterpartId = getCounterpartId(document, values);
      const vatBooksById = new Map(vatBooks.map((book) => [book.id, book]));
      const companySpecies = await ensureCompanySpecies(companyId);
      const { bookkeepingAccountId: _, ...primaryRegData } = values.registration;
      await createVATRegistration({
        companyId,
        ...primaryRegData,
        counterpartId,
        entries: mapEntriesToPayload(
          entriesForRegistration(
            values.entries,
            primaryRegData.vatBookId,
            values.reverseCharge,
            document,
            vatBooksById,
            vatCodesById,
          ),
          vatCodesById,
          companySpecies,
          primaryRegData.date,
          direction === DocumentDirection.received,
          !values.secondaryRegistration,
          document?.documentType === DocumentType.CreditNote,
          (type, variant) => t(`transactions.create.form.adjustment_type.${type}.${variant}`),
        ),
        bookkeepingTransaction,
        number,
      });

      if (values.secondaryRegistration && secondaryNumber) {
        const { bookkeepingAccountId: _, ...secondaryRegData } = values.secondaryRegistration;
        await createVATRegistration({
          companyId,
          ...secondaryRegData,
          counterpartId,
          entries: mapEntriesToPayload(
            entriesForRegistration(
              values.entries,
              secondaryRegData.vatBookId,
              values.reverseCharge,
              document,
              vatBooksById,
              vatCodesById,
            ),
            vatCodesById,
            companySpecies,
            secondaryRegData.date,
            direction === DocumentDirection.received,
            true,
            document?.documentType === DocumentType.CreditNote,
            (type, variant) => t(`transactions.create.form.adjustment_type.${type}.${variant}`),
          ),
          bookkeepingTransaction:
            hasSelfInvoiceBookepingRegistrationException(eInvoiceType) || values.reverseCharge
              ? undefined
              : bookkeepingTransaction,
          number: secondaryNumber,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DocumentQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: VATRegistrationsQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: VATEntriesQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: BookkeepingInvoicesQueryKeys.allAIVATRegistrationProposals });
    },
    onError: (error) => {
      captureException(error);
    },
  });
};

export interface CreateMultipleVATRegistrationPayload {
  document: Document;
  eInvoice?: ItalianEInvoice | null;
  values: VATRegistrationFormOutput;
}

export const useCreateMultipleVATRegistrations = () => {
  const companyId = useActiveCompanyId();
  const generateBookkeepingEntries = useBookkeepingEntriesForVATRegistration();
  const { data: vatBooks } = useVATBooks();
  const { data: vatCodesById } = useVATCodesById();
  const { t } = useTranslation('bookkeeping');

  return useMutation({
    mutationFn: async (payloads: CreateMultipleVATRegistrationPayload[]) => {
      const vatBooksById = new Map(vatBooks.map((book) => [book.id, book]));
      const companySpecies = await ensureCompanySpecies(companyId);
      // Avoiding parallel requests to maintain sequential numbering
      for (const { document, eInvoice, values } of payloads) {
        const number = await fetchVATRegistrationNumber(companyId, values.registration.vatBookId);
        const secondaryNumber = await fetchVATRegistrationNumber(
          companyId,
          values.secondaryRegistration?.vatBookId ?? null,
        );

        const eInvoiceType =
          eInvoice && getEInvoiceBody(eInvoice, document.number!).datiGenerali.datiGeneraliDocumento.tipoDocumento;

        const bookkeepingEntries = generateBookkeepingEntries({
          document,
          direction: document.direction,
          hasVatRegistrationException:
            hasSelfInvoiceBookepingRegistrationException(eInvoiceType) || values.reverseCharge,
          vatEntries: values.entries,
          counterpartId: values.registration.counterpartId,
        });

        const bookkeepingTransaction: Omit<CreateBookkeepingTransaction, 'companyId'> = {
          date: values.registration.date,
          description: values.description,
          bookkeepingEntries,
          transactionIds: [],
        };

        const counterpartId = getCounterpartId(document, values);
        const { bookkeepingAccountId: _, ...primaryRegData } = values.registration;
        await createVATRegistration({
          companyId,
          ...primaryRegData,
          counterpartId,
          entries: mapEntriesToPayload(
            entriesForRegistration(
              values.entries,
              primaryRegData.vatBookId,
              values.reverseCharge,
              document,
              vatBooksById,
              vatCodesById,
            ),
            vatCodesById,
            companySpecies,
            primaryRegData.date,
            document.direction === DocumentDirection.received,
            !values.secondaryRegistration,
            document.documentType === DocumentType.CreditNote,
            (type, variant) => t(`transactions.create.form.adjustment_type.${type}.${variant}`),
          ),
          bookkeepingTransaction,
          number,
        });

        if (values.secondaryRegistration && secondaryNumber) {
          const { bookkeepingAccountId: _, ...secondaryRegData } = values.secondaryRegistration;
          await createVATRegistration({
            companyId,
            ...secondaryRegData,
            counterpartId,
            entries: mapEntriesToPayload(
              entriesForRegistration(
                values.entries,
                secondaryRegData.vatBookId,
                values.reverseCharge,
                document,
                vatBooksById,
                vatCodesById,
              ),
              vatCodesById,
              companySpecies,
              secondaryRegData.date,
              document.direction === DocumentDirection.received,
              true,
              document.documentType === DocumentType.CreditNote,
              (type, variant) => t(`transactions.create.form.adjustment_type.${type}.${variant}`),
            ),
            bookkeepingTransaction,
            number: secondaryNumber,
          });
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DocumentQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: VATRegistrationsQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: VATEntriesQueryKeys.all });
    },
    onError: (error) => {
      captureException(error);
    },
  });
};

interface UpdateVatRegistrationPayload {
  document: Document | null;
  direction: DocumentDirection;
  primaryRegistration: VATRegistration;
  secondaryRegistration: VATRegistration | undefined;
  values: VATRegistrationFormOutput;
}

const getPrimaryRegistrationEntriesForUpdate = (
  registrationEntries: UpdateVATEntry[],
  primaryRegistration: VATRegistration,
  secondaryRegistration: VATRegistration | undefined,
) => {
  if (!secondaryRegistration) {
    return registrationEntries;
  }

  // For reverse charge the form is seeded from the secondary (PURCHASES) leg, so each form
  // entry carries the purchases-leg id. Correlate the two existing legs by content (a
  // reverse-charge line shares vat code + taxable + tax) to recover the matching SALES entry
  // id, preserving its identity and any potentially linked entity (e.g. fixed assets).
  const vatEntryContentKey = (entry: VATRegistrationEntry): string =>
    `${entry.vatCode.id}|${entry.taxableAmount.amount}|${entry.taxAmount.amount}`;
  const salesIdByContent = new Map(
    primaryRegistration.vatEntries.map((entry) => [vatEntryContentKey(entry), entry.id]),
  );
  const salesIdByPurchaseId = new Map(
    secondaryRegistration.vatEntries.map(
      (entry) => [entry.id, salesIdByContent.get(vatEntryContentKey(entry)) ?? null] as const,
    ),
  );

  return registrationEntries.map((entry) => ({
    ...entry,
    id: entry.id ? (salesIdByPurchaseId.get(entry.id) ?? null) : null,
  }));
};

export const useUpdateVATRegistration = () => {
  const companyId = useActiveCompanyId();
  const generateBookkeepingEntries = useBookkeepingEntriesForVATRegistration();
  const { data: vatBooks } = useVATBooks();
  const { data: vatCodesById } = useVATCodesById();
  const { t } = useTranslation('bookkeeping');
  return useMutation({
    mutationFn: async ({
      document,
      direction,
      primaryRegistration,
      secondaryRegistration,
      values,
    }: UpdateVatRegistrationPayload) => {
      const bookkeepingEntries = generateBookkeepingEntries({
        document,
        direction,
        hasVatRegistrationException: values.reverseCharge,
        vatEntries: values.entries,
        bookkeepingAccountId: values.registration.bookkeepingAccountId,
        counterpartId: values.registration.counterpartId,
      });
      const counterpartId = getCounterpartId(document, values);
      const vatBooksById = new Map(vatBooks.map((book) => [book.id, book]));
      const companySpecies = await ensureCompanySpecies(companyId);
      const bookkeepingTransaction = primaryRegistration.bookkeepingTransaction;
      assertIsDefined(bookkeepingTransaction, 'Bookkeeping transaction is required for updating');
      const registrationEntries = mapEntriesToPayload(
        entriesForRegistration(
          values.entries,
          values.registration.vatBookId,
          values.reverseCharge,
          document,
          vatBooksById,
          vatCodesById,
        ),
        vatCodesById,
        companySpecies,
        values.registration.date,
        direction === DocumentDirection.received,
        !secondaryRegistration,
        document?.documentType === DocumentType.CreditNote,
        (type, variant) => t(`transactions.create.form.adjustment_type.${type}.${variant}`),
      ) as UpdateVATEntry[];

      await updateVATRegistration(primaryRegistration.id, {
        date: values.registration.date,
        entries: getPrimaryRegistrationEntriesForUpdate(
          registrationEntries,
          primaryRegistration,
          secondaryRegistration,
        ),
        reportingPeriodId: values.registration.reportingPeriodId,
        vatBookId: values.registration.vatBookId,
        counterpartId,
        bookkeepingTransaction: {
          bookkeepingEntries,
          id: bookkeepingTransaction.id,
          description: values.description,
        },
      });

      if (secondaryRegistration && isDefined(values.secondaryRegistration)) {
        assertIsDefined(
          !secondaryRegistration.bookkeepingTransaction,
          'Secondary registration should not have a bookkeeping transaction',
        );

        await updateVATRegistration(secondaryRegistration.id, {
          date: values.secondaryRegistration.date,
          entries: mapEntriesToPayload(
            entriesForRegistration(
              values.entries,
              values.secondaryRegistration.vatBookId,
              values.reverseCharge,
              document,
              vatBooksById,
              vatCodesById,
            ),
            vatCodesById,
            companySpecies,
            values.secondaryRegistration.date,
            direction === DocumentDirection.received,
            true,
            document?.documentType === DocumentType.CreditNote,
            (type, variant) => t(`transactions.create.form.adjustment_type.${type}.${variant}`),
          ),
          reportingPeriodId: values.secondaryRegistration.reportingPeriodId,
          vatBookId: values.secondaryRegistration.vatBookId,
          counterpartId,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DocumentQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: VATRegistrationsQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: VATEntriesQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: BookkeepingInvoicesQueryKeys.allAIVATRegistrationProposals });
    },
    onError: (error) => {
      captureException(error);
    },
  });
};

export const useVATRegistrationNumber = (vatBookId: VATBook['id'] | null) => {
  const companyId = useActiveCompanyId();
  return useQuery({
    queryKey: VATRegistrationsQueryKeys.latestNumber(companyId, vatBookId),
    queryFn: () => fetchVATRegistrationNumber(companyId, vatBookId),
  });
};

export const usePreviewVATRegistrationNumber = () => {
  return useMutation({
    mutationFn: previewVATRegistrationNumber,
  });
};

export const useDeleteVATRegistration = () => {
  return useMutation({
    mutationFn: async (registration: VATRegistration) => {
      const documentId = registration.documents[0]?.id;
      if (documentId) {
        await deleteVATRegistrationsByDocumentId(documentId);
      } else {
        await deleteVATRegistrationById(registration.id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DocumentQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: VATRegistrationsQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: BookkeepingTransactionQueryKeys.all });
    },
  });
};

export const useBillsToRegister = (filters: FetchBillsToRegisterFilters) => {
  const companyId = useActiveCompanyId();
  return useQuery({
    queryKey: VATRegistrationsQueryKeys.billsToRegister(companyId, filters),
    queryFn: () => fetchBillsToRegister(companyId, filters),
    enabled: !!companyId,
  });
};

export const useVATRegistrationBulkRegister = () =>
  useMutation({
    mutationFn: bulkBookkeepingTransactionsRegistration,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DocumentQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: VATRegistrationsQueryKeys.all });
    },
  });

export const useUpdateBillVATRegistration = () => {
  return useMutation({
    mutationFn: async ({
      vatRegistrationId,
      payload,
    }: {
      vatRegistrationId: VATRegistration['id'];
      payload: UpdateVATRegistration;
    }) => {
      await updateVATRegistration(vatRegistrationId, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DocumentQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: VATRegistrationsQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: VATEntriesQueryKeys.all });
    },
    onError: (error) => {
      captureException(error);
    },
  });
};

export const fetchDocumentForVatRegistration = async (
  id: Document['id'],
  { useAiProposals }: { useAiProposals: boolean },
): Promise<DocumentWithVATRegistrations> => {
  const documents = await fetchDocument(id, { include: ['vatRegistrations'] });

  const documentWithVatRegistrations: DocumentWithVATRegistrations = {
    ...documents,
    vatRegistrations: documents.vatRegistrations ?? [],
  };

  const [documentWithEInvoice] = await decorateDocumentsWithEInvoices([documentWithVatRegistrations]);
  if (!documentWithEInvoice) {
    return documentWithVatRegistrations;
  }

  const decorator = useAiProposals
    ? decorateDocumentsWithAIVATRegistrationProposal
    : decorateDocumentsWithVATRegistrationProposal;

  const [documentWithVATRegistrationProposal] = await decorator([documentWithEInvoice]);

  return documentWithVATRegistrationProposal ?? documentWithEInvoice;
};
